/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo, useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { IconGift, IconPlus, IconTrash, IconX } from "@tabler/icons-react";
import { useProductListDataById } from "../../../apis/promoApis";
import {
  useMerchantAllListData,
  useMerchantListData,
  useAllFMMerchantData,
} from "../../../apis/merchantApis";
import { useSelector } from "react-redux";
import { useLanguage } from "../../../context/LanguageContext";
import DatePicker from "../../../components/DatePicker";
import PremiumDateRangePicker from "../../../components/PremiumDateRangePicker";
import NewinputField from "../../../components/NewInput";
import NewSelectInput from "../../../components/NewSelectInput";
import toast from "react-hot-toast";

interface AssignDriversModalProps {
  open: boolean;
  onClose: () => void;
  inline?: boolean;
  hideHeader?: boolean;
  editData?: any;
  onSubmit?: (values: any) => void;
}

const CreateGiftForm = forwardRef<{ submit: () => void }, AssignDriversModalProps>(
  ({ open, onClose, inline = false, hideHeader = false, editData, onSubmit: parentSubmit }, ref) => {
    const { t } = useLanguage();

    // Merchant data sources
    const reduxClients = useSelector((state: any) => state?.client?.data);
    const { data: apiClientsResponse } = useMerchantAllListData();
    const { data: listClientsResponse } = useMerchantListData(1, 200, "", "MERCHANT");
    const { data: fmClientsResponse } = useAllFMMerchantData();

    // Form state
    const [merchantId, setMerchantId] = useState("");
    const [merchant, setMerchant] = useState("");
    const [mainProductSku, setMainProductSku] = useState("");
    const [mainQty, setMainQty] = useState(1);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [giftList, setGiftList] = useState<Array<{ sku: string; qty: number }>>([
      { sku: "", qty: 1 },
    ]);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const todayStr = useMemo(() => new Date().toISOString().split("T")[0], []);

    // Products for selected merchant
    const { data: merchantProductsResponse } = useProductListDataById(merchantId, true);

    // Helper to extract client list recursively from any payload structure
    const extractClients = (sourceData: any): any[] => {
      if (!sourceData) return [];
      if (Array.isArray(sourceData)) return sourceData;
      if (Array.isArray(sourceData?.data)) return sourceData.data;
      if (Array.isArray(sourceData?.clients)) return sourceData.clients;
      if (Array.isArray(sourceData?.data?.clients)) return sourceData.data.clients;
      if (Array.isArray(sourceData?.lmClients)) return sourceData.lmClients;
      if (Array.isArray(sourceData?.data?.lmClients)) return sourceData.data.lmClients;
      if (Array.isArray(sourceData?.merchants)) return sourceData.merchants;
      if (Array.isArray(sourceData?.data?.merchants)) return sourceData.data.merchants;
      if (Array.isArray(sourceData?.items)) return sourceData.items;
      if (Array.isArray(sourceData?.data?.items)) return sourceData.data.items;
      if (typeof sourceData === "object") {
        for (const key of Object.keys(sourceData)) {
          if (Array.isArray(sourceData[key])) return sourceData[key];
        }
      }
      return [];
    };

    // Filter merchants with FM or BOTH access
    const filteredMerchants = useMemo(() => {
      const rawAll = extractClients(apiClientsResponse);
      const rawList = extractClients(listClientsResponse);
      const rawFm = extractClients(fmClientsResponse);
      const rawRedux = extractClients(reduxClients);

      const merged = [...rawFm, ...rawAll, ...rawList, ...rawRedux];
      const map = new Map<string, any>();

      merged.forEach((c: any) => {
        if (!c) return;
        const rawAccess = c?.access || c?.lmClient?.access;
        if (rawAccess !== "FM" && rawAccess !== "BOTH") return;

        const name = (
          c.companyName ||
          c.company_name ||
          c.name ||
          c.contactPerson ||
          c.contact_person ||
          c.merchantName ||
          c.clientName ||
          c.title ||
          c.email ||
          c.id ||
          ""
        ).toString().trim();

        const idVal = String(c.id || c.clientId || name);
        if (name && !map.has(idVal)) {
          map.set(idVal, { ...c, displayName: name, idValue: idVal });
        }
      });

      return Array.from(map.values());
    }, [apiClientsResponse, listClientsResponse, fmClientsResponse, reduxClients]);

    // Extract parent & variant SKUs
    const extractSkus = (items: any[]): Array<{ id: string; name: string; sku: string }> => {
      if (!Array.isArray(items)) return [];
      const list: Array<{ id: string; name: string; sku: string }> = [];
      items.forEach((p: any) => {
        const parentName = (p?.itemName || p?.name || p?.title || "Product").toString();
        const rawSku = p?.sku && p?.sku !== "N/A" ? p.sku : p?.productSku || p?.code || "";
        const finalSku = (rawSku || parentName || p?.id || "").toString();
        if (finalSku && finalSku !== "N/A") {
          list.push({ id: String(p?.id || finalSku), name: parentName, sku: finalSku });
        }
        if (Array.isArray(p?.variants) && p.variants.length > 0) {
          p.variants.forEach((v: any) => {
            const vSku = (v?.sku || v?.optionValue || "").toString();
            const vName = v?.optionName ? `${parentName} (${v.optionName})` : parentName;
            if (vSku && vSku !== "N/A") {
              list.push({ id: String(v?.id || vSku), name: vName, sku: vSku });
            }
          });
        }
      });
      return list;
    };

    const availableProducts = useMemo<any[]>(() => {
      if (!merchantId) return [];
      const productsSource = Array.isArray(merchantProductsResponse)
        ? merchantProductsResponse
        : Array.isArray(merchantProductsResponse?.data)
        ? merchantProductsResponse.data
        : Array.isArray(merchantProductsResponse?.productItems)
        ? merchantProductsResponse.productItems
        : [];
      const extracted = extractSkus(productsSource);
      const map = new Map<string, { id: string; name: string; sku: string }>();
      extracted.forEach((item) => {
        if (item.sku && !map.has(item.sku)) map.set(item.sku, item);
      });
      return Array.from(map.values());
    }, [merchantId, merchantProductsResponse]);

    const isMerchantSelected = Boolean(merchantId);

    const maxGiftsAllowed = availableProducts.length;
    const canAddMoreGifts =
      isMerchantSelected && availableProducts.length > 0 && giftList.length < maxGiftsAllowed;

    // Get available complimentary gift options for a specific row
    // Allows the base/trigger SKU to also be selected as a complimentary gift
    const getAvailableGiftProductsForIndex = (currentIdx: number) => {
      if (!isMerchantSelected) return [];
      const otherSelectedSkus = giftList
        .filter((_, i) => i !== currentIdx)
        .map((g) => g.sku)
        .filter(Boolean);
      return availableProducts.filter((p: any) => {
        if (otherSelectedSkus.includes(p.sku)) return false;
        return true;
      });
    };

    // Pre-fill form when editing
    useEffect(() => {
      if (open && editData) {
        // Try clientId first, then merchantId/toClient
        const resolvedMerchantId = editData.clientId || editData.merchantId || editData.toClient || "";
        setMerchantId(resolvedMerchantId);
        setMerchant(editData.merchant || "");
        setMainProductSku(editData.mainProductSku || "");
        setMainQty(editData.mainQty || 1);
        setStartDate(editData.startDate || "");
        setEndDate(editData.endDate || "");
        const prefilledGifts = Array.isArray(editData.giftItems) && editData.giftItems.length > 0
          ? editData.giftItems.map((g: any) => ({ sku: g.sku || g.giftProductSku || "", qty: Number(g.qty || g.giftQty) || 1 }))
          : [{ sku: editData.giftProductSku || "", qty: Number(editData.giftQty) || 1 }];
        setGiftList(prefilledGifts);
        setErrors({});
      } else if (open && !editData) {
        setMerchantId("");
        setMerchant("");
        setMainProductSku("");
        setMainQty(1);
        setStartDate("");
        setEndDate("");
        setGiftList([{ sku: "", qty: 1 }]);
        setErrors({});
      }
    }, [open, editData]);

    // Re-resolve merchantId and merchant name once filteredMerchants loads (API is async)
    // Also handles old records where clientId was not saved — falls back to matching by name
    useEffect(() => {
      if (editData && filteredMerchants.length > 0) {
        const rawClientId = editData.clientId || editData.merchantId || editData.toClient || "";
        const merchantName = editData.merchant || "";

        // Try to find by ID first, then by name
        const found =
          filteredMerchants.find((c: any) => c.idValue === rawClientId || c.id === rawClientId) ||
          (merchantName
            ? filteredMerchants.find(
                (c: any) =>
                  (c.displayName || "").toLowerCase() === merchantName.toLowerCase()
              )
            : undefined);

        if (found) {
          setMerchantId(found.idValue);
          setMerchant(found.displayName);
        }
      }
    }, [filteredMerchants, editData]);

    const handleTriggerSkuChange = (val: string) => {
      setMainProductSku(val);
    };

    const handleGiftChange = (index: number, field: "sku" | "qty", value: any) => {
      const updated = [...giftList];
      updated[index] = { ...updated[index], [field]: value };
      setGiftList(updated);
      if (field === "sku" && errors.giftProductSku) {
        setErrors((prev) => ({ ...prev, giftProductSku: "" }));
      }
      if (field === "qty" && errors.giftQty) {
        setErrors((prev) => ({ ...prev, giftQty: "" }));
      }
    };

    const addGiftRow = () => {
      if (!canAddMoreGifts) return;
      setGiftList((prev) => [...prev, { sku: "", qty: 1 }]);
    };

    const removeGiftRow = (index: number) => {
      if (giftList.length <= 1) return;
      setGiftList((prev) => prev.filter((_, i) => i !== index));
    };

    const validate = () => {
      const newErrors: Record<string, string> = {};
      if (!merchantId) newErrors.merchantId = t("Please select Merchant");
      if (!mainProductSku) newErrors.mainProductSku = t("Please select Purchase Trigger SKU");
      if (!mainQty || mainQty < 1) newErrors.mainQty = t("Quantity must be at least 1");
      if (!giftList[0]?.sku) newErrors.giftProductSku = t("Please select Complimentary Gift SKU");
      if (!giftList[0]?.qty || giftList[0].qty < 1) newErrors.giftQty = t("Quantity must be at least 1");
      if (!startDate) newErrors.startDate = t("Start Date is required");
      if (!endDate) newErrors.endDate = t("End Date is required");
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (!validate()) return;

      const validGiftItems = giftList.filter((g) => g.sku.trim());
      const values = {
        toClient: merchantId,
        merchantId,
        merchant,
        giftSku: editData?.giftSku || "",
        mainProductSku,
        mainQty,
        giftProductSku: validGiftItems[0]?.sku || "",
        giftQty: validGiftItems[0]?.qty || 1,
        giftItems: validGiftItems,
        startDate,
        endDate,
      };

      if (parentSubmit) {
        parentSubmit(values);
      } else {
        toast.success(t("Gift created successfully"));
        onClose();
      }
    };

    useImperativeHandle(ref, () => ({
      submit: () => {
        const syntheticEvent = { preventDefault: () => {} } as React.FormEvent;
        handleSubmit(syntheticEvent);
      },
    }));

    if (!open) return null;

    const formContent = (
      <form noValidate onSubmit={handleSubmit}>
        {!inline && !hideHeader && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-[#212121]">
              {editData ? t("Edit Gift Rule") : t("Add Gift Rule")}
            </h2>
            <div className="flex items-center gap-3">
              <button
                type="submit"
                className="px-4 py-1.5 text-xs font-bold bg-[#FBD037] border border-[#FBD037] text-[#212121] rounded-lg hover:bg-yellow-400 transition"
              >
                {editData ? t("Update Gift Rule") : t("Deploy Gift Rule")}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700"
                title="Close"
              >
                <IconX size={16} stroke={1.5} />
              </button>
            </div>
          </div>
        )}

        <div className={`flex-1 space-y-2 ${inline ? "p-2 overflow-visible" : "p-5 max-h-[75vh] overflow-y-auto"}`}>
          
          {/* Merchant */}
          <div className="grid grid-cols-2 gap-5">
            <NewSelectInput
              name="merchantId"
              label={t("Merchant")}
              validation={{ required: t("Merchant is required") }}
              placeholder={t("Select Merchant")}
              value={merchantId}
              error={errors.merchantId ? { message: errors.merchantId } : undefined}
              selectOptions={filteredMerchants.map((c: any) => ({
                id: c.idValue,
                value: c.idValue,
                text: c.displayName,
              }))}
              onChange={(val) => {
                const selected = filteredMerchants.find((c: any) => c.idValue === val || c.id === val);
                setMerchantId(val);
                setMerchant(selected ? selected.displayName : "");
                setMainProductSku("");
                setGiftList([{ sku: "", qty: 1 }]);
                if (errors.merchantId) setErrors((prev) => ({ ...prev, merchantId: "" }));
              }}
            />
             <PremiumDateRangePicker
            title={t("Start & End Date")}
            startDate={startDate}
            endDate={endDate}
            startDateError={errors.startDate}
            endDateError={errors.endDate}
            onChangeRange={({ startDate: sDate, endDate: eDate }) => {
              setStartDate(sDate);
              setEndDate(eDate);
              if (errors.startDate || errors.endDate) {
                setErrors((prev) => ({ ...prev, startDate: "", endDate: "" }));
              }
            }}
          />
          </div>

          {/* Purchase Trigger & Complimentary Gift */}
          <div className="flex flex-col space-y-4  p-1 rounded-2xl">
            {/* Purchase Trigger */}
            <div className="space-y-1">
              <div className="grid grid-cols-12 gap-2 items-start">
                <div className="col-span-9">
                  <NewSelectInput
                    name="mainProductSku"
                    label={t("Purchase Trigger")}
                    placeholder={isMerchantSelected ? t("Select Product SKU") : t("Select Merchant First")}
                    disabled={!isMerchantSelected}
                    value={mainProductSku}
                    error={errors.mainProductSku ? { message: errors.mainProductSku } : undefined}
                    selectOptions={
                      isMerchantSelected
                        ? [
                            ...(mainProductSku && !availableProducts.some((p: any) => p.sku === mainProductSku)
                              ? [{ id: mainProductSku, value: mainProductSku, text: mainProductSku }]
                              : []),
                            ...availableProducts.map((p: any) => ({
                              id: p.id || p.sku,
                              value: p.sku,
                              text: p.name && p.name !== p.sku ? `${p.sku} — ${p.name}` : p.sku,
                            })),
                          ]
                        : []
                    }
                    onChange={(val) => {
                      handleTriggerSkuChange(val);
                      if (errors.mainProductSku) setErrors((prev) => ({ ...prev, mainProductSku: "" }));
                    }}
                  />
                </div>
                <div className="col-span-3">
                  <NewinputField
                    title={t("Qty")}
                    name="mainQty"
                    type="number"
                    min={1}
                    disabled={!isMerchantSelected}
                    value={mainQty}
                    error={errors.mainQty ? { message: errors.mainQty } : undefined}
                    other={{
                      onChange: (e: any) => {
                        const val = Math.max(1, Number(e.target.value));
                        setMainQty(val);
                        if (errors.mainQty) setErrors((prev) => ({ ...prev, mainQty: "" }));
                      },
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Complimentary Gift (mandatory) */}
            <div className="space-y-2 pt-1">
              <div className="flex justify-between items-center">
                <span className="text-xs font-medium text-[#B47814]">
                  {t("Complimentary Gift *")}
                </span>
                <button
                  type="button"
                  onClick={addGiftRow}
                  disabled={!canAddMoreGifts}
                  className={`text-xs font-semibold text-[#B47814] hover:text-amber-800 flex items-center gap-1 transition ${
                    !canAddMoreGifts ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                  }`}
                >
                  <IconPlus size={14} /> {t("Add SKU")}
                </button>
              </div>

              <div className="space-y-2">
                {giftList.map((item, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-start">
                    <div className={giftList.length > 1 ? "col-span-8" : "col-span-9"}>
                      <NewSelectInput
                        name={`giftSku_${idx}`}
                        label={t("Gift SKU")}
                        placeholder={isMerchantSelected ? t("Select Gift SKU") : t("Select Merchant First")}
                        disabled={!isMerchantSelected}
                        value={item.sku}
                        selectOptions={
                          isMerchantSelected
                            ? [
                                ...(item.sku && !availableProducts.some((p: any) => p.sku === item.sku)
                                  ? [{ id: item.sku, value: item.sku, text: item.sku }]
                                  : []),
                                ...getAvailableGiftProductsForIndex(idx).map((p: any) => ({
                                  id: p.id || p.sku,
                                  value: p.sku,
                                  text: p.name && p.name !== p.sku ? `${p.sku} — ${p.name}` : p.sku,
                                })),
                              ]
                            : []
                        }
                        onChange={(val) => handleGiftChange(idx, "sku", val)}
                      />
                    </div>
                    <div className="col-span-3">
                      <NewinputField
                        title={t("Qty")}
                        name={`giftQty_${idx}`}
                        type="number"
                        min={1}
                        disabled={!isMerchantSelected}
                        value={item.qty}
                        other={{
                          onChange: (e: any) =>
                            handleGiftChange(idx, "qty", Math.max(1, Number(e.target.value))),
                        }}
                      />
                    </div>
                    {giftList.length > 1 && (
                      <div className="col-span-1 pt-3 flex items-center justify-center">
                        <button
                          type="button"
                          onClick={() => removeGiftRow(idx)}
                          className="text-slate-400 hover:text-rose-500 transition p-1"
                        >
                          <IconTrash size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {errors.giftProductSku && <p className="text-xs text-red-500">{errors.giftProductSku}</p>}
              {errors.giftQty && <p className="text-xs text-red-500">{errors.giftQty}</p>}
            </div>
          </div>

          {/* Start & End Date */}
         
        </div>
      </form>
    );

    if (inline) return formContent;

    return ( 
        <div className="border rounded-lg border-gray-200 flex flex-col overflow-hidden bg-white/20 w-full">
          {formContent}
        </div> 
    );
  }
);

export default CreateGiftForm;
